import type { Participant } from '#src/pages/create/types.ts';
import type { CreateGroupRequest } from '@packages/contracts/group';
import { exists } from '@packages/primitives/exists';
import { invariant } from '@packages/primitives/invariant';

export interface BuildCreateGroupRequestInput {
  name: string;
  participants: Participant[];
  wantsToAddExclusions: boolean;
  /** Used for a blank name, e.g. "Secret Santa 2026". */
  defaultName: string;
}

export interface BuiltCreateGroupRequest {
  request: CreateGroupRequest;
  exclusionCount: number;
}

/** Converts the create form into a request, resolving exclusions from form ids to names. */
export function buildCreateGroupRequest({
  name,
  participants,
  wantsToAddExclusions,
  defaultName,
}: BuildCreateGroupRequestInput): BuiltCreateGroupRequest {
  const requestParticipants = participants.map(participant => {
    const excludedNames = participant.exclusions.map(exclusion =>
      participants.find(p => p.id === exclusion)?.name.trim(),
    );
    invariant(
      excludedNames.every(excludedName => exists(excludedName)),
      'Excluded name not found',
    );
    return {
      name: participant.name.trim(),
      excludedNames: wantsToAddExclusions ? excludedNames : [],
    };
  });

  const exclusionCount = wantsToAddExclusions
    ? participants.reduce((sum, participant) => sum + participant.exclusions.length, 0)
    : 0;

  return {
    request: { name: name.trim() || defaultName, participants: requestParticipants },
    exclusionCount,
  };
}
